import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosDelete, axiosGet, axiosPost, axiosPut } from '../utils/api';

const invalidateCommissionData = (queryClient, id) => {
  queryClient.invalidateQueries({ queryKey: ['/commission'] });
  if (id) {
    queryClient.invalidateQueries({ queryKey: ['/commission', id, 'history'] });
  }
};

export const getAllCommissions = ({ accessToken, params, config }) =>
  queryOptions({
    queryKey: ['/commission', params],
    queryFn: async () => {
      const res = await axiosGet('/commission', { params }, accessToken);
      return res.data;
    },
    ...config,
  });

export const getCommissionHistory = ({ id, accessToken, config } = {}) =>
  queryOptions({
    queryKey: ['/commission', id, 'history'],
    queryFn: async () => {
      const res = await axiosGet(`/commission/${id}/history`, {}, accessToken);
      return res.data;
    },
    enabled: Boolean(id),
    staleTime: 0,
    refetchOnMount: 'always',
    ...config,
  });

export const useCreateCommission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }) => {
      return axiosPost('/commission', data);
    },
    onSuccess: (res) => {
      invalidateCommissionData(queryClient, res?.data?._id);
    },
  });
};

export const useUpdateCommission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => {
      return axiosPut(`/commission/${id}`, data);
    },
    onSuccess: (_res, variables) => {
      invalidateCommissionData(queryClient, variables?.id);
    },
  });
};

export const useApproveCommission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => {
      return axiosPut(`/commission/${id}/approve`, {});
    },
    onSuccess: (_res, variables) => {
      invalidateCommissionData(queryClient, variables?.id);
    },
  });
};

export const useDeclineCommission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => {
      return axiosPut(`/commission/${id}/decline`, data);
    },
    onSuccess: (_res, variables) => {
      invalidateCommissionData(queryClient, variables?.id);
    },
  });
};

export const useUnlockCommission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => {
      return axiosPut(`/commission/${id}/unlock`, data || {});
    },
    onSuccess: (_res, variables) => {
      invalidateCommissionData(queryClient, variables?.id);
    },
  });
};

export const useSetCommissionPaid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => {
      return axiosPut(`/commission/${id}/paid`, data);
    },
    onSuccess: (_res, variables) => {
      invalidateCommissionData(queryClient, variables?.id);
    },
  });
};

export const useDeleteCommission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => {
      return axiosDelete(`/commission/${id}`);
    },
    onSuccess: (_res, variables) => {
      invalidateCommissionData(queryClient, variables?.id);
    },
  });
};
